import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const TOP_LINKS = [
  { to: "/", label: "Home" },
  { to: "/talent", label: "Cloud & Data Talent" },
  { to: "/join", label: "Join" },
];

const RESOURCES_MENU = [
  { to: "/articles", label: "Articles", desc: "Field notes on senior nearshore hiring" },
  { to: "/technologies", label: "Technologies", desc: "The senior Cloud & Data stack we vet for" },
  { to: "/calculator", label: "Cost Calculator", desc: "What your data team really costs, fully loaded" },
  { to: "/glossary", label: "Glossary", desc: "44 staffing terms, plainly defined" },
  { to: "/faq", label: "FAQ", desc: "Straight answers, including pricing" },
];

const RESOURCES_PREFIXES = ["/resources", "/articles", "/technologies", "/calculator", "/glossary", "/faq"];

type SiteNavProps = { onQuote: () => void };

export default function SiteNav({ onQuote }: SiteNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const resourcesActive = RESOURCES_PREFIXES.some((p) => pathname.startsWith(p));
  const isV2 = pathname === "/" || pathname === "/home-v2";

  const linkClass = (isActive: boolean) =>
    `group relative whitespace-nowrap text-[13px] font-medium transition-colors duration-300 ${
      isActive ? "text-white" : "text-white/70 hover:text-white"
    }`;

  return (
    <div className={isV2 ? "absolute inset-x-0 top-0 z-40" : "bg-hero-gradient"} data-testid="site-nav">
      <nav className={`mx-auto flex max-w-6xl items-center justify-between px-5 sm:px-8 ${isV2 ? "py-4 lg:py-6 lg:border-b lg:border-white/25" : "border-b border-white/25 py-6"}`}>
        <Link to="/" className="leading-none" data-testid="nav-logo">
          <span className="block font-display text-3xl font-extrabold lowercase tracking-tight text-white">
            nearcore
          </span>
          <span className="mt-1 block text-[11px] font-medium tracking-[0.18em] text-white/75">
            Cloud &amp; Data Talent
          </span>
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {TOP_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
              className={({ isActive }) => linkClass(isActive)}
            >
              {link.label}
              <span
                className={`absolute -bottom-1 left-0 h-px bg-white transition-[width] duration-300 ${
                  pathname === link.to ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </NavLink>
          ))}

          <div className="group relative">
            <button
              type="button"
              data-testid="nav-link-resources"
              className={`group relative flex items-center gap-1 whitespace-nowrap text-[13px] font-medium transition-colors duration-300 ${
                resourcesActive ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              Resources
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
              <span
                className={`absolute -bottom-1 left-0 h-px bg-white transition-[width] duration-300 ${
                  resourcesActive ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </button>
            <div
              className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100"
              data-testid="nav-dropdown-resources"
            >
              <div className="overflow-hidden rounded-xl bg-white shadow-2xl shadow-brand-teal/20 ring-1 ring-brand-teal/10">
                {RESOURCES_MENU.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-dropdown-link-${item.label.toLowerCase()}`}
                    className="block px-5 py-3.5 transition-colors duration-200 hover:bg-brand-mist"
                  >
                    <span className="block font-display text-sm font-bold text-brand-teal">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-brand-slate/80">{item.desc}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <motion.button
            type="button"
            onClick={onQuote}
            data-testid="nav-quote-button"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="rounded-md bg-brand-orange px-5 py-2.5 text-[13px] font-bold text-white shadow-lg shadow-black/20 transition-colors duration-300 hover:bg-brand-orange-dark"
          >
            Get a custom quote
          </motion.button>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-menu-toggle"
          onClick={() => setMenuOpen((v) => !v)}
          className="text-white lg:hidden"
        >
          {menuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`overflow-hidden border-b border-white/25 lg:hidden ${isV2 ? "bg-[#03383F]" : ""}`}
            data-testid="nav-mobile-menu"
          >
            <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-6 sm:px-8">
              {TOP_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-medium ${isActive ? "text-white" : "text-white/75"}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <p className="mt-2 font-cond text-[11px] font-bold uppercase tracking-[0.22em] text-white/50">
                Resources
              </p>
              {RESOURCES_MENU.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `pl-4 text-sm font-medium ${isActive ? "text-white" : "text-white/75"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onQuote();
                }}
                data-testid="nav-mobile-quote-button"
                className="mt-2 w-fit rounded-md bg-brand-orange px-5 py-2.5 text-[13px] font-bold text-white"
              >
                Get a custom quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
